import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, Copy, RotateCw } from 'lucide-react'
import { useDocumentStore } from '../stores/useDocumentStore'

type Props = {
  open: boolean
  onClose: () => void
}

export function ShareDialog({ open, onClose }: Props) {
  const doc = useDocumentStore((s) => s.doc)
  const setShared = useDocumentStore((s) => s.setShared)
  const rotateSlug = useDocumentStore((s) => s.rotateSlug)

  const [working, setWorking] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !doc) return null

  const shareUrl =
    doc.is_public && doc.share_slug ? `${window.location.origin}/g/${doc.share_slug}` : ''

  const toggle = async (enabled: boolean) => {
    setWorking(true)
    try {
      await setShared(enabled)
    } finally {
      setWorking(false)
    }
  }

  const rotate = async () => {
    setWorking(true)
    try {
      await rotateSlug()
    } finally {
      setWorking(false)
    }
  }

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-foreground/10 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-background border border-border rounded-lg p-6 shadow-lg">
          <button
            className="absolute top-3 right-3 p-1 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <h2
            className="text-[18px] font-light tracking-tight mb-1"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            Share document
          </h2>
          <p
            className="text-[12px] text-muted-foreground font-light mb-6"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            Anyone with the link can view this document, read-only.
          </p>

          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={doc.is_public}
                onChange={(e) => toggle(e.target.checked)}
                disabled={working}
                className="w-4 h-4"
              />
              <span
                className="text-[13px] font-light"
                style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
              >
                Public link enabled
              </span>
            </label>
          </div>

          {doc.is_public && doc.share_slug && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 bg-muted/40 border border-border rounded-md px-3 py-2 text-[12px] font-mono outline-none select-all"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={copy}
                  className="flex items-center gap-1 border border-border px-3 py-2 rounded-md text-[11px] font-light hover:bg-muted/50"
                  style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                >
                  {copied ? (
                    <Check className="w-3 h-3" strokeWidth={1.5} />
                  ) : (
                    <Copy className="w-3 h-3" strokeWidth={1.5} />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button
                onClick={rotate}
                disabled={working}
                className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light disabled:opacity-50"
                style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
              >
                <RotateCw className="w-3 h-3" strokeWidth={1.5} />
                Rotate link (old one will stop working)
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
